import React, { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { Button, Modal } from '../../../components';
import recruitmentService from '../../../services/carehome_manager/recruitmentService';
import {
  Section, inputCls, fieldLabelCls, fmtDate, toDateInput,
} from './shared';

/**
 * "Manager checks & reference review" — the organisation's own decisions.
 *
 * Deliberately one component shown on BOTH candidate steps, because the
 * decisions are about the candidate rather than about a step: a manager
 * reviewing personal details who realises the Right to Work evidence was thin
 * should not have to navigate back to record that.
 *
 * The rule this section exists to hold: RECEIVING A DOCUMENT IS NOT CHECKING
 * IT. Nothing here is derived. Four Right to Work documents can arrive and the
 * status stays "Pending review" until a person chooses otherwise, and their
 * name is stamped on the choice.
 *
 * Two rows, and only two: the right to work and the DBS. They are the two
 * things that must be DECIDED while somebody is still a candidate. Occupational
 * health, professional registration and identity verification are post-offer
 * admin and are recorded against the staff record in Add & Edit Staff, not
 * here — there is nothing a manager can act on until the person exists.
 *
 * Every field in the dialog comes from MANAGER_CHECK_DEFINITIONS on the server,
 * so the compliance record has one definition and this file never keeps its own
 * copy of the list.
 *
 */
const ManagerChecksSection = ({ candidate, definitions, busy, locked, run }) => {
  const [reviewing, setReviewing] = useState(null);
  const [reviewForm, setReviewForm] = useState({});

  const checks = candidate.checks || [];
  const answers = candidate.pre_interview?.answers || {};

  const answerLabel = (list, value) => list?.find((option) => option.value === value)?.label || '';

  const openReview = (check) => {
    setReviewing(check);
    setReviewForm({
      status: check.status,
      note: check.note || '',
      date: toDateInput(check.date),
      expiry: toDateInput(check.expiry),
      fields: Object.fromEntries((check.fields || []).map((field) => [
        field.name,
        field.type === 'date' ? toDateInput(field.value) : field.value,
      ])),
    });
  };

  const setField = (name, value) => setReviewForm((form) => ({
    ...form,
    fields: { ...form.fields, [name]: value },
  }));

  // The supporting fields are laid out in the SLOTS the compliance dialog uses
  // — document type, reference number, reminder schedule — so both dialogs put
  // the same thing in the same place. The server names each one's slot; see
  // MANAGER_CHECK_DEFINITIONS in api/utils/recruitment.js.
  const slotField = (slot) => (reviewing?.fields || []).find((field) => field.slot === slot);
  const slotValue = (slot) => {
    const field = slotField(slot);
    return field ? (reviewForm.fields?.[field.name] ?? '') : '';
  };
  // Anything the server adds later without a slot still gets rendered, rather
  // than silently disappearing from the dialog.
  const unslottedFields = (reviewing?.fields || []).filter((field) => !field.slot);

  const saveReview = async () => {
    const response = await run(
      () => recruitmentService.setManagerCheck(candidate.id, reviewing.id, reviewForm),
      { successMessage: `${reviewing.label} decision saved` },
    );
    if (response) setReviewing(null);
  };

  return (
    <>
      <Section
        icon={ShieldCheck}
        title="Manager checks"
        subtitle="Receiving a file does not verify it. Keep these decisions separate from the candidate's answers."
      >
        <div className="divide-y divide-[#F1F5F9] dark:divide-gray-800 border border-[#E3E9F3] dark:border-gray-700 rounded-xl overflow-hidden mb-5">
          {checks.map((check) => (
            <div key={check.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold text-[#0B1220] dark:text-gray-100">{check.label}</div>
                <div className="text-[12.5px] mt-0.5">
                  <span className={check.decided
                    ? 'text-[#475569] dark:text-gray-300 font-semibold'
                    : 'text-amber-600 dark:text-amber-400 font-semibold'}
                  >
                    {check.status}
                  </span>
                  {check.decided && check.date && (
                    <span className="text-[#64748B] dark:text-gray-400"> · {fmtDate(check.date)}</span>
                  )}
                  {check.reviewed_by && (
                    <span className="text-[#64748B] dark:text-gray-400"> · by {check.reviewed_by}</span>
                  )}
                  <span className="text-[#94A3B8] dark:text-gray-500">
                    {` · ${check.documents_received} document${check.documents_received === 1 ? '' : 's'} received`}
                  </span>
                </div>
                {check.note && (
                  <div className="text-[12.5px] text-[#475569] dark:text-gray-300 mt-1.5">{check.note}</div>
                )}
              </div>
              <Button variant="secondary" size="sm" disabled={locked} onClick={() => openReview(check)}>
                Review check
              </Button>
            </div>
          ))}
        </div>

      </Section>

      <Modal
        isOpen={Boolean(reviewing)}
        onClose={() => setReviewing(null)}
        title={reviewing ? `Review: ${reviewing.label}` : ''}
        maxWidth="max-w-xl"
        footer={(
          <div className="flex justify-end gap-2.5">
            <Button variant="secondary" onClick={() => setReviewing(null)}>Cancel</Button>
            <Button variant="primary" loading={busy} onClick={saveReview}>Save decision</Button>
          </div>
        )}
      >
        {reviewing && (
          <div className="space-y-4">
            <p className="text-[13px] text-[#475569] dark:text-gray-300">
              This is your decision on the evidence, recorded against {candidate.name}. The candidate
              cannot see or change it.
            </p>

            {reviewing.candidate_answer && (
              <div className="p-3.5 rounded-xl bg-[#F8FAFC] dark:bg-[#2c323b] border border-[#E3E9F3] dark:border-gray-700">
                <div className="text-[11.5px] font-bold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500 mb-1">
                  The candidate said
                </div>
                <div className="text-[13px] text-[#0B1220] dark:text-gray-200">
                  {reviewing.id === 'rtw'
                    ? answerLabel(definitions?.rtwProvision, answers.rtw_provision)
                    : answerLabel(definitions?.dbsAvailability, answers.dbs_availability)}
                  {reviewing.id === 'rtw' && answers.rtw_share_code
                    ? ` · share code ${answers.rtw_share_code}` : ''}
                </div>
              </div>
            )}

            {/* Field for field, the Update Compliance Check dialog in Staff
                Compliance: same boxes, same order, same labels, same dropdowns.
                The two write THE SAME COLUMNS (CHECK_MAP in
                api/utils/complianceSync.js), so a manager who records a DBS
                while hiring somebody meets the same form again in Staff
                Compliance afterwards instead of a second, different one.

                The two boxes that dialog has and this one does not:
                  STAFF MEMBER — this one is opened from one candidate's record.
                  PROOF ATTACHMENT — already attached. The candidate sent the
                  evidence with their pre-interview form, and it travels to the
                  staff record at transfer as the proof behind the mirrored
                  compliance row. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {slotField('document_type') && (
                <div>
                  <label className={fieldLabelCls} htmlFor="check-document-type">
                    {slotField('document_type').label}
                  </label>
                  <input
                    id="check-document-type"
                    className={inputCls}
                    value={slotValue('document_type')}
                    placeholder={slotField('document_type').placeholder || ''}
                    onChange={(event) => setField(slotField('document_type').name, event.target.value)}
                  />
                </div>
              )}

              <div>
                {/* "Status" over in Staff Compliance. Called Decision here
                    because that is what it is at this point — a person's verdict
                    on the evidence, not a state the system worked out. The words
                    are the check's own, and complianceSync maps them onto the
                    compliance table's status. */}
                <label className={fieldLabelCls} htmlFor="review-status">Decision</label>
                <select
                  id="review-status"
                  className={inputCls}
                  value={reviewForm.status}
                  onChange={(event) => setReviewForm((form) => ({ ...form, status: event.target.value }))}
                >
                  {reviewing.outcomes.map((outcome) => (
                    <option key={outcome} value={outcome}>{outcome}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={fieldLabelCls} htmlFor="review-date">{reviewing.date_label}</label>
                <input
                  id="review-date"
                  type="date"
                  className={inputCls}
                  value={reviewForm.date}
                  onChange={(event) => setReviewForm((form) => ({ ...form, date: event.target.value }))}
                />
              </div>
              <div>
                <label className={fieldLabelCls} htmlFor="review-expiry">{reviewing.expiry_label}</label>
                <input
                  id="review-expiry"
                  type="date"
                  className={inputCls}
                  value={reviewForm.expiry}
                  onChange={(event) => setReviewForm((form) => ({ ...form, expiry: event.target.value }))}
                />
              </div>

              {slotField('reference_no') && (
                <div>
                  <label className={fieldLabelCls} htmlFor="check-reference">
                    {slotField('reference_no').label}
                  </label>
                  <input
                    id="check-reference"
                    className={inputCls}
                    value={slotValue('reference_no')}
                    placeholder={slotField('reference_no').placeholder || ''}
                    onChange={(event) => setField(slotField('reference_no').name, event.target.value)}
                  />
                </div>
              )}

              {slotField('reminder_schedule') && (
                <div>
                  {/* A dropdown, with the same four options and the same stored
                      values Staff Compliance offers — this was a free text box,
                      so a typed "90, 60" ended up in a column that screen reads
                      as a schedule. */}
                  <label className={fieldLabelCls} htmlFor="check-reminder">
                    {slotField('reminder_schedule').label}
                  </label>
                  <select
                    id="check-reminder"
                    className={inputCls}
                    value={slotValue('reminder_schedule')}
                    onChange={(event) => setField(slotField('reminder_schedule').name, event.target.value)}
                  >
                    {/* A record saved before this was a dropdown holds prose the
                        list does not contain. Keep it selectable rather than
                        silently re-pointing it at the first option. */}
                    {!slotField('reminder_schedule').options
                      .some((option) => option.value === slotValue('reminder_schedule')) && (
                      <option value={slotValue('reminder_schedule')}>
                        {slotValue('reminder_schedule') || 'Choose a schedule'}
                      </option>
                    )}
                    {slotField('reminder_schedule').options.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {unslottedFields.map((field) => {
                const id = `check-field-${field.name}`;
                const value = reviewForm.fields?.[field.name];
                return (
                  <div key={field.name} className={field.type === 'textarea' ? 'sm:col-span-2' : ''}>
                    <label className={fieldLabelCls} htmlFor={id}>{field.label}</label>
                    <input
                      id={id}
                      type={field.type === 'date' ? 'date' : 'text'}
                      className={inputCls}
                      value={value || ''}
                      placeholder={field.placeholder}
                      onChange={(event) => setField(field.name, event.target.value)}
                    />
                  </div>
                );
              })}

              <div className="sm:col-span-2">
                {/* "Manager notes" there, Notes here — same column either way. */}
                <label className={fieldLabelCls} htmlFor="review-note">Manager notes</label>
                <textarea
                  id="review-note"
                  rows={3}
                  className={inputCls}
                  value={reviewForm.note}
                  placeholder="What you checked, and what it showed."
                  onChange={(event) => setReviewForm((form) => ({ ...form, note: event.target.value }))}
                />
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ManagerChecksSection;
